/**
 * Parallel merchant product-card enhancements
 *
 * Adds to canonical Parallel product cards:
 *
 * 1. Discount percentage badge
 * 2. Add to Cart
 * 3. Proceed to Checkout
 * 4. Continue Shopping
 *
 * Canonical card selector:
 *
 * li[data-parallel-product-card="true"][data-product-id]
 *
 * This intentionally excludes predictive-search cards.
 */

(function () {
  'use strict';


  /* =========================================================
     CONFIG
     ========================================================= */

  const SELECTOR_PRODUCT_CARD =
    'li[data-parallel-product-card="true"][data-product-id]';

  const SELECTOR_PRICE_CONTAINER =
    '.product-price';

  let storefrontToken = null;
  let observerStarted = false;


  /* =========================================================
     HELPERS
     ========================================================= */

  function parseMoney(value) {
    if (!value) {
      return NaN;
    }

    const cleaned = String(value)
      .replace(/,/g, '')
      .replace(/[^0-9.]/g, '');

    return parseFloat(cleaned);
  }


  function getRootUrl() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return '/';
  }


  function getCheckoutUrl() {
    const rootUrl = getRootUrl();

    return (
      rootUrl +
      (
        rootUrl.endsWith('/')
          ? 'checkout'
          : '/checkout'
      )
    );
  }


  function isParallelProductCard(element) {
    return !!(
      element &&
      element.nodeType === 1 &&
      element.matches &&
      element.matches(SELECTOR_PRODUCT_CARD)
    );
  }


  function normalizeProductGid(value) {
    const raw =
      String(value || '').trim();

    if (!raw) {
      return '';
    }

    if (
      raw.indexOf(
        'gid://shopify/Product/'
      ) === 0
    ) {
      return raw;
    }

    if (/^\d+$/.test(raw)) {
      return (
        'gid://shopify/Product/' +
        raw
      );
    }

    return '';
  }


  function normalizeVariantIdForCart(value) {
    const raw =
      String(value || '').trim();

    if (!raw) {
      return '';
    }

    /*
     * Shopify cart/add.js expects the numeric
     * variant ID rather than the GraphQL GID.
     */
    if (
      raw.indexOf(
        'gid://shopify/ProductVariant/'
      ) === 0
    ) {
      return raw.split('/').pop();
    }


    if (/^\d+$/.test(raw)) {
      return raw;
    }


    /*
     * Defensive fallback in case another
     * representation contains the numeric ID.
     */
    const match =
      raw.match(/(\d{5,})/);

    return match
      ? match[1]
      : '';
  }


  function getProductIdForForm(card) {
    const raw =
      String(
        card.getAttribute(
          'data-product-id'
        ) || ''
      ).trim();

    if (!raw) {
      return '';
    }

    if (
      raw.indexOf(
        'gid://shopify/Product/'
      ) === 0
    ) {
      return raw.split('/').pop();
    }

    return raw;
  }


  /*
   * Clean up anything injected by the earlier
   * broad [data-product-id] implementation.
   *
   * Predictive search is not a canonical
   * data-parallel-product-card.
   */
  function cleanupPredictiveSearch() {
    document
      .querySelectorAll(
        '.gs-predictive-search .injected-atc-form, ' +
        '.gs-predictive-search .parallel-product-card-actions, ' +
        '.gs-predictive-search .p-sale-label'
      )
      .forEach(function (element) {
        element.remove();
      });
  }


  /* =========================================================
     DISCOUNT BADGE
     ========================================================= */

  function injectSaleLabel(card) {
    if (!isParallelProductCard(card)) {
      return;
    }


    const priceContainer =
      card.querySelector(
        SELECTOR_PRICE_CONTAINER
      );


    if (!priceContainer) {
      return;
    }


    const compareAtSpan =
      priceContainer.querySelector(
        '.compare-at-price'
      );


    /*
     * No compare-at price means this card
     * isn't currently discounted.
     */
    if (!compareAtSpan) {
      const existingLabel =
        card.querySelector(
          '.p-sale-label'
        );

      if (existingLabel) {
        existingLabel.remove();
      }

      return;
    }


    /*
     * The current Parallel card renders the
     * current price as the normal span and the
     * RRP as .compare-at-price.
     */
    const priceSpans =
      Array.from(
        priceContainer.querySelectorAll(
          'span'
        )
      );


    const currentPriceSpan =
      priceSpans.find(
        function (span) {
          return !span.classList.contains(
            'compare-at-price'
          );
        }
      );


    const currentPrice =
      currentPriceSpan
        ? parseMoney(
            currentPriceSpan.textContent
          )
        : parseMoney(
            priceContainer
              .childNodes[0]
              ?.textContent
          );


    const comparePrice =
      parseMoney(
        compareAtSpan.textContent
      );


    if (
      !Number.isFinite(currentPrice) ||
      !Number.isFinite(comparePrice) ||
      currentPrice <= 0 ||
      comparePrice <= currentPrice
    ) {
      const existingLabel =
        card.querySelector(
          '.p-sale-label'
        );

      if (existingLabel) {
        existingLabel.remove();
      }

      return;
    }


    const discountPercent =
      Math.round(
        (
          (
            comparePrice -
            currentPrice
          ) /
          comparePrice
        ) * 100
      );


    if (discountPercent <= 0) {
      return;
    }


    let label =
      card.querySelector(
        '.p-sale-label'
      );


    if (!label) {
      label =
        document.createElement(
          'span'
        );

      label.className =
        'p-sale-label';


      card.insertAdjacentElement(
        'afterbegin',
        label
      );
    }


    label.textContent =
      '- ' +
      discountPercent +
      '%';
  }


  function injectSaleLabels(root) {
    const scope =
      root || document;


    if (
      isParallelProductCard(scope)
    ) {
      injectSaleLabel(scope);
    }


    if (!scope.querySelectorAll) {
      return;
    }


    scope
      .querySelectorAll(
        SELECTOR_PRODUCT_CARD
      )
      .forEach(
        injectSaleLabel
      );
  }


  /* =========================================================
     STOREFRONT FALLBACK
     ========================================================= */

  async function getFirstVariant(
    productId
  ) {
    const productGid =
      normalizeProductGid(
        productId
      );


    if (!productGid) {
      return null;
    }


    const response =
      await fetch(
        'https://' +
          window.location.hostname +
          '/api/2025-04/graphql.json',
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            'X-Shopify-Storefront-Access-Token':
              storefrontToken
          },

          body: JSON.stringify({
            query: `
              query ParallelProductVariant($id: ID!) {
                node(id: $id) {
                  ... on Product {
                    id

                    variants(first: 1) {
                      edges {
                        node {
                          id
                          availableForSale
                        }
                      }
                    }
                  }
                }
              }
            `,

            variables: {
              id: productGid
            }
          })
        }
      );


    if (!response.ok) {
      throw new Error(
        'Storefront API returned ' +
        response.status
      );
    }


    const json =
      await response.json();


    if (
      json.errors &&
      json.errors.length
    ) {
      throw new Error(
        json.errors[0].message
      );
    }


    const node =
      json?.data?.node;


    if (
      !node ||
      !node.variants ||
      !node.variants.edges ||
      !node.variants.edges.length
    ) {
      return null;
    }


    return (
      node
        .variants
        .edges[0]
        .node
    );
  }


  /* =========================================================
     VARIANT RESOLUTION
     ========================================================= */

  async function resolveVariantId(card) {
    /*
     * Preferred route:
     *
     * ProductCardFeatures already renders
     * data-variant-id onto the canonical card.
     */
    const cardVariantId =
      normalizeVariantIdForCart(
        card.getAttribute(
          'data-variant-id'
        )
      );


    if (cardVariantId) {
      return cardVariantId;
    }


    /*
     * Fallback only if the rendered product
     * didn't contain a variant ID.
     */
    const productId =
      card.getAttribute(
        'data-product-id'
      );


    if (!productId) {
      return '';
    }


    const variant =
      await getFirstVariant(
        productId
      );


    if (
      !variant ||
      !variant.id
    ) {
      return '';
    }


    return normalizeVariantIdForCart(
      variant.id
    );
  }


  /* =========================================================
     POST ADD STATE
     ========================================================= */

  function renderPostAddActions(form) {
    if (!form) {
      return;
    }


    form.classList.remove(
      'is-loading'
    );


    /*
     * IMPORTANT:
     *
     * ProductCardFeatures considers anything inside
     * .injected-atc-form an Add to Cart click.
     *
     * Once the product has actually been added, remove
     * that tracking hook so checkout / continue clicks
     * aren't falsely recorded as more ATC events.
     */
    form.classList.remove(
      'injected-atc-form'
    );


    form.classList.add(
      'parallel-post-add-form'
    );


    form.innerHTML = `
      <div class="parallel-post-add-actions">

        <p class="parallel-added-message">
          Added to cart
        </p>

        <button
          type="button"
          class="parallel-checkout-button"
        >
          Proceed to Checkout
        </button>

        <button
          type="button"
          class="parallel-continue-shopping"
        >
          Continue Shopping
        </button>

      </div>
    `;


    const checkoutButton =
      form.querySelector(
        '.parallel-checkout-button'
      );


    const continueButton =
      form.querySelector(
        '.parallel-continue-shopping'
      );


    if (checkoutButton) {
      checkoutButton.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          event.stopPropagation();


          window.location.assign(
            getCheckoutUrl()
          );
        }
      );
    }


    if (continueButton) {
      continueButton.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          event.stopPropagation();


          window.location.reload();
        }
      );
    }
  }


  /* =========================================================
     ADD TO CART FORM
     ========================================================= */

  function createAddToCartForm(
    card,
    variantId
  ) {
    const productId =
      getProductIdForForm(
        card
      );


    const form =
      document.createElement(
        'form'
      );


    form.method =
      'post';


    form.action =
      '/cart/add';


    form.acceptCharset =
      'UTF-8';


    /*
     * injected-atc-form is intentionally present during
     * the actual Add to Cart state so the platform's
     * existing product-card tracking recognises it.
     */
    form.className =
      'product-item__action-list ' +
      'button-stack ' +
      'injected-atc-form ' +
      'parallel-product-card-actions';


    form.enctype =
      'multipart/form-data';


    form.innerHTML = `
      <input
        type="hidden"
        name="form_type"
        value="product"
      >

      <input
        type="hidden"
        name="utf8"
        value="✓"
      >

      <input
        type="hidden"
        name="quantity"
        value="1"
      >

      <input
        type="hidden"
        name="id"
        value="${variantId}"
      >

      <buy-button class="buy-button">
        <button
          type="submit"
          class="parallel-add-to-cart"
          data-parallel-product-card-atc="true"
        >
          Add to Cart
        </button>
      </buy-button>

      <input
        type="hidden"
        name="product-id"
        value="${productId}"
      >
    `;


    return form;
  }


  /* =========================================================
     ADD TO CART INJECTION
     ========================================================= */

  async function injectAddToCartButton(
    card
  ) {
    /*
     * Only canonical Parallel cards.
     */
    if (
      !isParallelProductCard(
        card
      )
    ) {
      return;
    }


    /*
     * Already has actions.
     */
    if (
      card.querySelector(
        '.parallel-product-card-actions'
      )
    ) {
      return;
    }


    /*
     * Prevent duplicate async variant requests.
     */
    if (
      card.dataset
        .parallelAtcLoading ===
      'true'
    ) {
      return;
    }


    card.dataset.parallelAtcLoading =
      'true';


    try {
      const variantId =
        await resolveVariantId(
          card
        );


      if (!variantId) {
        delete card.dataset
          .parallelAtcLoading;

        return;
      }


      /*
       * Card may have disappeared while the
       * fallback request was running.
       */
      if (
        !document.contains(card) ||
        !isParallelProductCard(
          card
        )
      ) {
        return;
      }


      /*
       * Another observer pass may have completed
       * while we were resolving the variant.
       */
      if (
        card.querySelector(
          '.parallel-product-card-actions'
        )
      ) {
        return;
      }


      const form =
        createAddToCartForm(
          card,
          variantId
        );


      /*
       * The canonical product card's product link
       * wraps the image/details. Append the form to
       * the LI so the button itself isn't inside
       * the PDP link.
       */
      card.appendChild(
        form
      );


      bindAddToCartForm(
        form
      );


      card.dataset
        .parallelAtcLoading =
        'complete';

    } catch (error) {
      console.error(
        '[Parallel] Error injecting Add to Cart:',
        error
      );


      delete card.dataset
        .parallelAtcLoading;
    }
  }


  /* =========================================================
     ADD TO CART BEHAVIOUR
     ========================================================= */

  function bindAddToCartForm(
    form
  ) {
    if (
      !form ||
      form.dataset.parallelBound ===
        'true'
    ) {
      return;
    }


    form.dataset.parallelBound =
      'true';


    /*
     * Don't let clicks inside the action area
     * trigger the product-card PDP link.
     */
    form.addEventListener(
      'click',
      function (event) {
        event.stopPropagation();
      }
    );


    form.addEventListener(
      'submit',
      async function (event) {
        event.preventDefault();
        event.stopPropagation();


        if (
          form.classList.contains(
            'is-loading'
          )
        ) {
          return;
        }


        const button =
          form.querySelector(
            '.parallel-add-to-cart'
          );


        form.classList.add(
          'is-loading'
        );


        if (button) {
          button.disabled =
            true;

          button.textContent =
            'Adding...';
        }


        try {
          const formData =
            new FormData(
              form
            );


          const rootUrl =
            getRootUrl();


          const addUrl =
            rootUrl +
            (
              rootUrl.endsWith('/')
                ? 'cart/add.js'
                : '/cart/add.js'
            );


          const response =
            await fetch(
              addUrl,
              {
                method: 'POST',

                body: formData,

                headers: {
                  Accept:
                    'application/json'
                }
              }
            );


          if (!response.ok) {
            let errorMessage =
              'Cart request returned ' +
              response.status;


            try {
              const errorPayload =
                await response.json();


              if (
                errorPayload &&
                (
                  errorPayload.description ||
                  errorPayload.message
                )
              ) {
                errorMessage =
                  errorPayload.description ||
                  errorPayload.message;
              }

            } catch (error) {
              /*
               * Ignore JSON parse errors and use
               * the HTTP status message instead.
               */
            }


            throw new Error(
              errorMessage
            );
          }


          const cartItem =
            await response.json();


          /*
           * Tell merchant themes / other listeners
           * that the cart has changed.
           */
          document.documentElement
            .dispatchEvent(
              new CustomEvent(
                'product:added',
                {
                  bubbles: true,

                  detail: {
                    quantity: 1,
                    variant: cartItem
                  }
                }
              )
            );


          /*
           * Don't automatically open the cart drawer.
           *
           * Instead the product card itself becomes:
           *
           * Added to cart
           * Proceed to Checkout
           * Continue Shopping
           */
          renderPostAddActions(
            form
          );

        } catch (error) {
          console.error(
            '[Parallel] Add to cart failed:',
            error
          );


          form.classList.remove(
            'is-loading'
          );


          if (button) {
            button.disabled =
              false;

            button.textContent =
              'Add to Cart';
          }
        }
      }
    );
  }


  function injectAddToCartButtons(
    root
  ) {
    const scope =
      root || document;


    if (
      isParallelProductCard(
        scope
      )
    ) {
      injectAddToCartButton(
        scope
      );
    }


    if (!scope.querySelectorAll) {
      return;
    }


    scope
      .querySelectorAll(
        SELECTOR_PRODUCT_CARD
      )
      .forEach(
        injectAddToCartButton
      );
  }


  /* =========================================================
     ENHANCE PRODUCT CARDS
     ========================================================= */

  function enhanceCards(root) {
    injectSaleLabels(
      root
    );


    injectAddToCartButtons(
      root
    );
  }


  /* =========================================================
     MUTATION OBSERVER
     ========================================================= */

  function startObserver() {
    if (observerStarted) {
      return;
    }


    observerStarted =
      true;


    const observer =
      new MutationObserver(
        function (mutations) {
          mutations.forEach(
            function (mutation) {
              if (
                !mutation
                  .addedNodes
                  .length
              ) {
                return;
              }


              mutation
                .addedNodes
                .forEach(
                  function (node) {
                    if (
                      node.nodeType !== 1
                    ) {
                      return;
                    }


                    enhanceCards(
                      node
                    );
                  }
                );
            }
          );
        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }


  /* =========================================================
     INIT
     ========================================================= */

  function runInjector(token) {
    if (!token) {
      return;
    }


    storefrontToken =
      token;


    /*
     * Clean anything left over from the
     * previous broad selector implementation.
     */
    cleanupPredictiveSearch();


    /*
     * Enhance every canonical Parallel card
     * already on the page.
     */
    enhanceCards(
      document
    );


    /*
     * Then catch cards subsequently rendered by
     * search, collections, personalisation, etc.
     */
    startObserver();
  }


  if (
    window.PARALLEL_PUBLIC_TOKEN
  ) {
    runInjector(
      window.PARALLEL_PUBLIC_TOKEN
    );

  } else {
    window.addEventListener(
      'parallelTokenReady',
      function (event) {
        runInjector(
          event.detail
        );
      },
      {
        once: true
      }
    );
  }


  /*
   * Fallback for cases where Parallel wrote the
   * public token before this custom JS loaded.
   */
  if (
    !window.PARALLEL_PUBLIC_TOKEN &&
    sessionStorage.getItem(
      'parallel_public_token'
    )
  ) {
    runInjector(
      sessionStorage.getItem(
        'parallel_public_token'
      )
    );
  }

})();
